import { Suspense, lazy, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useT } from '@/store';
import { useTenantId } from '@/hooks/useTenant';
import { api } from '@/services/api';
import { qk } from '@/lib/queryKeys';
import { QuotePreview } from './QuotePreview';
import { useDrivers, useMaterials, useAllQuarries, useSites } from '@/hooks/useDirectory';
import { SmartMatchList } from '@/components/dispatcher/SmartMatchList';
import { ClientBalanceNotice } from '@/components/clients/ClientBalanceNotice';
import { ListPriceNotice } from '@/components/orders/ListPriceNotice';
import { AddressSearchInput } from '@/components/ui/AddressSearchInput';

// Lazy for the same reason CoordinateField.jsx already reaches into the
// client portal for this: Leaflet is the largest dependency in this repo,
// and most order creations pick an existing site and never touch a map.
const SitePinPicker = lazy(() =>
  import('@/client/SitePinPicker').then((m) => ({ default: m.SitePinPicker })),
);

// Must stay in sync with core/utils.py's WEIGHT_CATEGORY_THRESHOLDS_TONS — a
// coarse tonnage-only proxy, since this form has no vehicle link to check
// against directly (see frontend/src/hooks/useFleet.js's
// VEHICLE_TYPE_REQUIRED_CATEGORY for the more precise check used once a
// specific vehicle is involved, e.g. on the Fleet page).
const WEIGHT_CATEGORY_THRESHOLDS_TONS = [
  [7.5, 'C'],
  [3.5, 'C1'],
];

function requiredLicenseCategoryForWeight(weightTons) {
  if (!Number.isFinite(weightTons)) return null;
  const hit = WEIGHT_CATEGORY_THRESHOLDS_TONS.find(([threshold]) => weightTons > threshold);
  return hit ? hit[1] : null;
}

/**
 * The one order-creation form body, shared by the dispatcher's
 * QuickDispatchModal and the Orders tab's CreateOrderDrawer — the monolith
 * had this markup twice, and the copies had already drifted: only one of
 * them populated the cargo datalist from the material catalogue.
 *
 * State and validation live in useOrderForm() (src/hooks), so both callers
 * get identical behavior and this file stays a pure view.
 *
 * The recommended-driver list (P2-3) lives here rather than in either
 * caller, for the same reason the fields do: it was wired into the client-
 * request approval modal ONLY, so the engine built to cut empty running was
 * invisible in the two flows that create almost every order. Putting it in
 * the shared body connects it to both at once, and keeps them from drifting
 * the way the duplicated markup already had.
 *
 * Field order changed with it: the driver used to be asked for first, above
 * the quarry. Nothing can recommend a driver before it knows where the load
 * starts, so the question that has an answer — which quarry — now comes
 * first, and the driver is chosen underneath the recommendations for it.
 */
export function OrderFormFields({ values, set, setValue }) {
  const t = useT();
  const { data: drivers = [] } = useDrivers();
  // Remembered from the recommendation that was clicked, and used for one
  // thing only: labelling the driver in the dropdown below if the dropdown
  // does not have them. See pickedNotListed.
  const [pickedName, setPickedName] = useState('');
  const { data: quarries = [] } = useAllQuarries();
  const { data: sites = [] } = useSites();
  const { data: materials = [] } = useMaterials();

  const selectedDriver = drivers.find((d) => d.driver_id === values.driver_id);
  const requiredCategory = requiredLicenseCategoryForWeight(Number.parseFloat(values.estimated_weight_tons));
  const missingCategory =
    selectedDriver && requiredCategory && !(selectedDriver.license_categories ?? []).includes(requiredCategory)
      ? requiredCategory
      : null;

  // The quarry's own material list is the better suggestion source when one
  // is picked; the tenant-wide catalogue is the fallback.
  const cargoSuggestions = useMemo(() => {
    const quarry = quarries.find((q) => q.quarry_id === values.quarry_id);
    const fromQuarry = String(quarry?.materials_available ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const fromCatalogue = materials.map((m) => m.name).filter(Boolean);
    return Array.from(new Set([...fromQuarry, ...fromCatalogue]));
  }, [quarries, materials, values.quarry_id]);

  // The two lists come from two separate requests — the driver directory and
  // the ranking — so they can disagree: a driver deactivated between the two
  // fetches is in one and not the other. Without an option to hold it, the
  // select silently falls back to showing "Auto" while the form state still
  // carries that driver's id, which is two controls showing one value as two
  // different answers. The dispatcher would submit a load to a driver the
  // screen told them was unassigned.
  const pickedNotListed =
    Boolean(values.driver_id) && !drivers.some((d) => d.driver_id === values.driver_id);

  // V-86. Which company this load is for. Undefined until a site is picked,
  // and undefined for a site whose client link was never made — see
  // blueprints/sites.py's _resolved_client_id(). The notice renders nothing
  // in both cases rather than guessing a company to warn about.
  const selectedClientId = useMemo(
    () => sites.find((s) => s.site_id === values.site_id)?.client_id,
    [sites, values.site_id],
  );

  return (
    <div className="space-y-3">
      <Field label={t('orders.quarryLabel')}>
        <select className="field" value={values.quarry_id} onChange={set('quarry_id')}>
          <option value="">—</option>
          {quarries.map((q) => (
            <option key={q.quarry_id} value={q.quarry_id}>
              {q.name}
            </option>
          ))}
        </select>
      </Field>

      <Field label={t('orders.siteLabel')}>
        <div className="mb-1.5 flex flex-wrap gap-2">
          <button
            type="button"
            className={`btn !px-2.5 !py-1 !text-[11px] ${values.location_mode === 'existing' ? 'btn-primary' : ''}`}
            aria-pressed={values.location_mode === 'existing'}
            onClick={() => setValue('location_mode', 'existing')}
          >
            {t('orders.locationExisting')}
          </button>
          <button
            type="button"
            className={`btn !px-2.5 !py-1 !text-[11px] ${values.location_mode === 'new' ? 'btn-primary' : ''}`}
            aria-pressed={values.location_mode === 'new'}
            onClick={() => setValue('location_mode', 'new')}
          >
            {t('orders.locationNew')}
          </button>
        </div>

        {values.location_mode === 'new' ? (
          <div className="space-y-2">
            <AddressSearchInput
              ariaLabel={t('orders.addressSearchLabel')}
              onSelect={(result) => {
                setValue('new_site_pin', { lat: result.lat, lng: result.lng });
                if (!values.new_site_name.trim()) setValue('new_site_name', result.label);
              }}
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                className="field"
                value={values.new_site_name}
                onChange={set('new_site_name')}
                placeholder={t('orders.newSiteNamePh')}
              />
              <input
                className="field"
                value={values.new_client_name}
                onChange={set('new_client_name')}
                placeholder={t('orders.newSiteClientNamePh')}
              />
            </div>
            <Suspense fallback={<div className="h-[260px] animate-pulse rounded-[11px] bg-white/5" />}>
              <SitePinPicker
                value={values.new_site_pin}
                onChange={(pin) => setValue('new_site_pin', pin)}
                radiusM={Number(values.new_site_radius_m) || 150}
                ariaLabel={t('orders.newSiteMapLabel')}
              />
            </Suspense>
            <p aria-live="polite" className="text-[10.5px] leading-snug text-dim">
              {values.new_site_pin
                ? t('orders.newSitePinSet', {
                    lat: values.new_site_pin.lat.toFixed(5),
                    lng: values.new_site_pin.lng.toFixed(5),
                  })
                : t('orders.newSitePinPrompt')}
            </p>
          </div>
        ) : (
          <select className="field" value={values.site_id} onChange={set('site_id')}>
            <option value="">—</option>
            {sites.map((s) => (
              <option key={s.site_id} value={s.site_id}>
                {s.client_name} · {s.site_name}
              </option>
            ))}
          </select>
        )}
      </Field>

      {/* V-86. The same figure as on the approval modal, at the same
          decision one step removed: this form promises a load to a site,
          and a site belongs to a company that may not have paid for the
          last one. The client is resolved from the picked site rather than
          asked for separately — the dispatcher chose a site, not a company,
          and making him name the company twice to be warned once is how a
          warning gets skipped. */}
      {selectedClientId ? <ClientBalanceNotice clientId={selectedClientId} /> : null}

      <Field label={t('orders.driverLabel')}>
        <select className="field" value={values.driver_id} onChange={set('driver_id')}>
          <option value="">{t('orders.autoDriverOption')}</option>
          {pickedNotListed ? (
            <option value={values.driver_id}>{pickedName || values.driver_id}</option>
          ) : null}
          {drivers.map((d) => (
            <option key={d.driver_id} value={d.driver_id}>
              {d.full_name}
              {/* V-227. license_plate, not vehicle_plate — see
                  blueprints/drivers.py's list_drivers() (V-225):
                  vehicle_plate is the driver's own raw, possibly-stale
                  fallback field, license_plate is the resolved truck
                  they're actually assigned to right now. */}
              {d.license_plate ? ` · ${d.license_plate}` : ''}
            </option>
          ))}
        </select>
      </Field>
      {missingCategory ? (
        <p className="text-[10.5px] text-amber">
          {t('drivers.licenseCategoryWarning', { category: missingCategory })}
        </p>
      ) : null}

      {/* Only once a quarry is picked: the ranking is "who is best placed to
          load HERE", so without one there is no question to answer. The
          dropdown above stays — a dispatcher who already knows which truck
          they want should not have to find it in a ranked list. */}
      {values.quarry_id && typeof setValue === 'function' ? (
        <div className="rounded-[11px] border border-line bg-white/2 p-3">
          <SmartMatchList
            quarryId={values.quarry_id}
            // V-100: the weight field is twenty lines below this list, and
            // this is the moment an overload can still be avoided for free.
            plannedTons={values.estimated_weight_tons}
            pickedDriverId={values.driver_id || undefined}
            onPick={(match) => {
              // Tapping the picked driver again clears it, back to Auto —
              // same gesture as the approval modal's list.
              const clearing = values.driver_id === match.driver_id;
              setPickedName(clearing ? '' : match.full_name);
              setValue('driver_id', clearing ? '' : match.driver_id);
            }}
          />
          {!values.driver_id ? (
            <p className="mt-2 text-[11px] text-dim">{t('smartMatch.autoNotice')}</p>
          ) : null}
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-3">
        <Field label={t('orders.cargoLabel')}>
          <input
            className="field"
            list="cargo-suggestions"
            value={values.cargo_type}
            onChange={set('cargo_type')}
            placeholder={t('orders.cargoComboPh')}
          />
          <datalist id="cargo-suggestions">
            {cargoSuggestions.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
        </Field>
        <Field label={t('orders.weightLabel')}>
          <input
            className="field"
            type="number"
            min="0"
            step="0.1"
            value={values.estimated_weight_tons}
            onChange={set('estimated_weight_tons')}
            placeholder={t('orders.weightPh')}
          />
        </Field>
      </div>

      {/* Kiek mašinų šito reikia. Dispečerio galvoje "5 mašinos žvyro" yra
          vienas darbas; šioje sistemoje vienas užsakymas = vienas reisas, ir
          tą skirtumą iki šiol reikėdavo suvesti ranka penkis kartus.
          Svoris lieka VIENO reiso, ne visos partijos: tai tonažas, kurį
          kiekvienas važtaraštis turės atitikti. */}
      <div className="grid grid-cols-2 gap-3">
        <Field label={t('orders.loadsLabel')}>
          <input
            className="field"
            type="number"
            min="1"
            step="1"
            value={values.loads}
            onChange={set('loads')}
            placeholder={t('orders.loadsPh')}
          />
        </Field>
        {/* Kada pažadėta. Tuščia reiškia "dabar" — toks yra kiekvienas
            užsakymas, suvestas iki šio lauko, ir taip jis ir elgiasi.
            Užpildyta ateities data užsakymas tampa REZERVACIJA: vairuotoją
            galima priskirti, bet jis lieka laisvas iki tos dienos, žinutės
            negauna ir telefone nieko nemato. Žr. core/order_liveness.py. */}
        <Field label={t('orders.scheduledForLabel')}>
          <input
            className="field"
            type="date"
            value={values.scheduled_for}
            onChange={set('scheduled_for')}
          />
        </Field>
      </div>
      {/* V-352. Valandų langas toje pačioje dienoje — abu galai arba nė
          vienas, tai patikrina serveris (core/delivery_window.py). Diena ir
          jos valandos stovi kartu, nes tai vienas pažadas, o ne du laukai:
          be lango reisas į punktualumo rodiklį nepatenka apskritai. */}
      <div className="grid grid-cols-2 gap-2">
        <Field label={t('orders.windowStartLabel')}>
          <input
            type="time"
            className="field"
            value={values.window_start}
            onChange={set('window_start')}
          />
        </Field>
        <Field label={t('orders.windowEndLabel')}>
          <input
            type="time"
            className="field"
            value={values.window_end}
            onChange={set('window_end')}
          />
        </Field>
      </div>
      <p className="-mt-1 text-[10.5px] leading-snug text-dim">{t('orders.windowHint')}</p>
      <LoadsNote loads={values.loads} driverId={values.driver_id} drivers={drivers} />
      <ReservationNote scheduledFor={values.scheduled_for} driverId={values.driver_id} />
      <DayCapacity day={values.scheduled_for} adding={values.loads} />

      {/* Transporto kainodaros modeliai: which of the five methods this
          load will be billed under. 'per_ton' (the default) is the
          agreed-price/price-list flow just below, unchanged; the other four
          replace it with their own fields — the same set POST
          .../pricing-method offers on an existing order, offered here too
          for a dispatcher who already knows the method at booking time and
          would rather not come back to set it after delivery. */}
      <Field label={t('orders.pricingMethodLabel')}>
        <select className="field" value={values.pricing_method} onChange={set('pricing_method')}>
          {['per_ton', 'per_km', 'flat', 'hourly', 'ton_km'].map((m) => (
            <option key={m} value={m}>{t(`pricingMethod.${m}`)}</option>
          ))}
        </select>
      </Field>

      {values.pricing_method === 'per_ton' ? (
        <>
          {/* V-35. Most of an SMB carrier's rates are agreed on the phone
              while the load is being booked, and until now there was
              nowhere to put one: the only field that reached
              orders.agreed_price_per_ton was on the B2B portal's approval
              form, which is the path fewest loads take. Optional, and the
              hint says what blank means rather than leaving an empty box to
              read as "no price". */}
          <Field label={t('orders.agreedPriceLabel')}>
            <input
              className="field"
              inputMode="decimal"
              value={values.agreed_price_per_ton}
              onChange={set('agreed_price_per_ton')}
              placeholder={t('orders.agreedPricePh')}
            />
          </Field>
          <p className="text-[10.5px] leading-snug text-dim">{t('orders.agreedPriceHint')}</p>
          {/* V-87. Directly under the sentence that promises "leave it blank
              and the price list decides", because that promise is only true
              when the list has a rate for this quarry and this material. It
              corrects the line above it in place, rather than making the
              normal case wordier by hedging the hint itself. */}
          <ListPriceNotice
            quarryId={values.quarry_id}
            cargoType={values.cargo_type}
            agreedPrice={values.agreed_price_per_ton}
          />
        </>
      ) : null}

      {values.pricing_method === 'per_km' ? (
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('modal.pricingDistancePh')}>
            <input className="field" inputMode="decimal" value={values.distance_km_one_way}
                  onChange={set('distance_km_one_way')} />
          </Field>
          <Field label={t('modal.pricingRatePerKmPh')}>
            <input className="field" inputMode="decimal" value={values.price_per_km}
                  onChange={set('price_per_km')} />
          </Field>
          <label className="col-span-2 flex items-center gap-2 text-[11.5px]">
            <input type="checkbox" checked={values.is_round_trip}
                  onChange={(e) => setValue('is_round_trip', e.target.checked)} />
            {t('modal.pricingRoundTrip')}
          </label>
        </div>
      ) : null}

      {values.pricing_method === 'flat' ? (
        <Field label={t('modal.pricingFlatPh')}>
          <input className="field" inputMode="decimal" value={values.flat_price_eur}
                onChange={set('flat_price_eur')} />
        </Field>
      ) : null}

      {values.pricing_method === 'hourly' ? (
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('modal.pricingHourlyRatePh')}>
            <input className="field" inputMode="decimal" value={values.hourly_rate_eur}
                  onChange={set('hourly_rate_eur')} />
          </Field>
          <Field label={t('modal.pricingBilledHoursPh')}>
            <input className="field" inputMode="decimal" value={values.billed_hours}
                  onChange={set('billed_hours')} />
          </Field>
        </div>
      ) : null}

      {values.pricing_method === 'ton_km' ? (
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('modal.pricingCapacityPh')}>
            <input className="field" inputMode="decimal" value={values.tonkm_capacity_tons}
                  onChange={set('tonkm_capacity_tons')} />
          </Field>
          <Field label={t('modal.pricingDistancePh')}>
            <input className="field" inputMode="decimal" value={values.distance_km_one_way}
                  onChange={set('distance_km_one_way')} />
          </Field>
        </div>
      ) : null}

      {/* V-352. Directly under the rate, because it is the answer to the
          number just typed: what this load keeps at that price. It renders
          nothing until the form can actually be quoted — a route, a
          material and a tonnage — so a half-filled form stays quiet. */}
      <QuotePreview
        quarryId={values.quarry_id}
        siteId={values.location_mode === 'new' ? null : values.site_id}
        cargoType={values.cargo_type}
        tons={values.estimated_weight_tons}
        pricePerTon={values.agreed_price_per_ton}
      />


      <Field label={t('orders.noteLabel')}>
        <textarea className="field min-h-[70px]" value={values.note} onChange={set('note')} placeholder={t('orders.notePh')} />
      </Field>
    </div>
  );
}

/**
 * Kas nutiks vairuotojui, kai rezervavime daugiau nei vienas reisas.
 *
 * Vienas vairuotojas gali turėti tik vieną atvirą užsakymą
 * (blueprints/orders.py create_order_record()), tad iš penkių reisų
 * įvardytas vairuotojas gauna VIENĄ, o likę keturi keliauja į automatinį
 * paskirstymą arba lieka laukti. Tai teisingas elgesys — bet dispečeris,
 * kuris pasirinko Gytį ir penkis reisus, be paaiškinimo pamatys keturis
 * užsakymus be vairuotojo ir palaikys tai klaida.
 *
 * Todėl sakoma PRIEŠ išsaugant, o ne aiškinama po to.
 */
function LoadsNote({ loads, driverId, drivers }) {
  const t = useT();
  const count = Number.parseInt(loads, 10);
  if (!Number.isFinite(count) || count <= 1) return null;
  const driver = drivers.find((d) => d.driver_id === driverId);
  return (
    <p className="text-[10.5px] text-dim">
      {driver
        ? t('orders.loadsNoteWithDriver', {
          loads: count, driver: driver.full_name, rest: count - 1,
        })
        : t('orders.loadsNoteAuto', { loads: count })}
    </p>
  );
}

/**
 * Kad „rezervacija" nebūtų netikėtumas.
 *
 * Užsakymas su vėlesne pristatymo diena elgiasi kitaip nei visi kiti:
 * lieka `scheduled`, vairuotojas nieko negauna, ir jo telefone reiso
 * nematyti iki tos dienos. Tai teisinga — bet dispečeris, pasirinkęs Lee ir
 * penktadienį, be paaiškinimo lauks Lee patvirtinimo, kurio nebus.
 *
 * Savaitgalio įspėjimas gyvena čia pat, o ne atskirai: abu atsako į tą patį
 * klausimą — ar pasirinkta diena tikrai ta, kurios norėta. „Po 5 dienų" nuo
 * trečiadienio nukrenta į sekmadienį, ir tai pastebima tik tada, kai kas
 * nors pasako garsiai.
 */
function ReservationNote({ scheduledFor, driverId }) {
  const t = useT();
  if (!scheduledFor) return null;
  // Vietinė diena iš pačios eilutės, ne per Date(): 'YYYY-MM-DD' Date
  // konstruktoriuje yra UTC vidurnaktis, ir rytų laiko juostose jis
  // pavadintų vakarykštę dieną.
  const [y, m, d] = scheduledFor.split('-').map(Number);
  if (!y || !m || !d) return null;
  const picked = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isFuture = picked > today;
  const weekend = picked.getDay() === 0 || picked.getDay() === 6;
  if (!isFuture && !weekend) return null;
  return (
    <p className="text-[10.5px] text-dim">
      {isFuture ? t(driverId ? 'orders.reservedWithDriver' : 'orders.reservedAuto') : null}
      {isFuture && weekend ? ' · ' : null}
      {weekend ? <span className="text-amber">{t('orders.weekendWarning')}</span> : null}
    </p>
  );
}

/**
 * Ar ta diena dar tilps.
 *
 * KODĖL ČIA, O NE KALENDORIUJE. Kalendorius, į kurį reikia prisiminti
 * pažiūrėti, perkrovos neišvengia — jis parodo ją tada, kai jau įvyko.
 * Vienintelė akimirka, kai perkrovos dar galima išvengti nemokamai, yra ta,
 * kai dispečeris renka skaičių, ir todėl atsakymas ateina čia.
 *
 * Skaičiuojami DU dalykai, ne vienas: kiek dienos jau paimta žmonių ir kiek
 * darbo dar neturi nieko. Diena su 100% apkrova ir septyniais nepriskirtais
 * reisais nėra pilna diena — tai dvi skirtingos problemos, ir sudėtos į
 * vieną procentą jos abi dingsta (core/load_balance.py grąžina jas atskirai
 * dėl tos pačios priežasties).
 *
 * Rodoma, niekada neblokuojama. Dispečeris gali žinoti tai, ko nežino
 * sistema — pasamdytą vežėją, sutrumpintą reisą, kliento sutikimą laukti —
 * ir forma, atsisakanti priimti užsakymą dėl skaičiaus, verstų jį eiti
 * aplinkui. Tas pats sprendimas, kurį core/invoicing.py priėmė svorio
 * nuokrypiui: "Shown, never blocked".
 */
function DayCapacity({ day, adding }) {
  const t = useT();
  const tenantId = useTenantId();
  const wanted = Number.parseInt(adding, 10);
  // Klausiama tik tada, kai yra apie ka klausti: pasirinkta diena arba
  // daugiau nei vienas reisas. Vienas krovinys siandien yra iprastas atvejis
  // ir savo atsakymo neturi — o uzklausa, siaunanti kiekviena karta atidarius
  // forma, yra tas pats susilaikymas, kuri sis failas jau taiko rekomendaciju
  // sarasui: nieko neklausiama, kol nezinoma, kur krovinys prasideda.
  const worthAsking = Boolean(day) || (Number.isFinite(wanted) && wanted > 1);
  const { data } = useQuery({
    queryKey: qk.loadBalance(tenantId, day || 'today'),
    queryFn: () => api(`/api/tenants/${tenantId}/load-balance${day ? `?date=${day}` : ''}`),
    enabled: Boolean(tenantId) && worthAsking,
    // Ta pati riba, kaip pačioje krūvio panelėje: sprendimas trunka mažiau
    // nei minutę, o du skirtingi šviežumai tam pačiam skaičiui dviejuose
    // ekranuose yra būdas priversti dispečerį nebetikėti nė vienu.
    staleTime: 60_000,
  });
  const summary = data?.summary;
  if (!summary) return null;

  const free = Math.max(0, (summary.drivers_working ?? 0) - (summary.orders ?? 0));
  const tight = Number.isFinite(wanted) && wanted > free;
  return (
    <p className={`text-[10.5px] ${tight ? 'text-amber' : 'text-dim'}`}>
      {t('orders.dayCapacity', {
        orders: summary.orders ?? 0,
        drivers: summary.drivers_working ?? 0,
        load: summary.average_load_pct ?? 0,
      })}
      {summary.unassigned_orders
        ? ` · ${t('orders.dayUnassigned', { count: summary.unassigned_orders })}`
        : ''}
      {tight ? ` · ${t('orders.dayTight', { free })}` : ''}
    </p>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[10.5px] font-bold uppercase tracking-[0.03em] text-dim">{label}</span>
      {children}
    </label>
  );
}
